import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Storefront, ShoppingCart, CaretLeft } from "@phosphor-icons/react";
import { cartCount } from "@/lib/shopCart";

const rp = (n) => "Rp " + (Number(n) || 0).toLocaleString("id-ID");

function priceRange(variants) {
  const prices = (variants || []).map((v) => (v.price_final != null ? v.price_final : v.price));
  if (!prices.length) return rp(0);
  const min = Math.min(...prices), max = Math.max(...prices);
  return min === max ? rp(min) : `${rp(min)} – ${rp(max)}`;
}

export default function PublicShop() {
  const [cats, setCats] = useState([]);
  const [products, setProducts] = useState([]);
  const [active, setActive] = useState("");
  const [count, setCount] = useState(cartCount());

  useEffect(() => {
    document.title = "Belanja — HalloGBS";
    api.get("/public/shop/categories").then((r) => setCats(r.data.categories)).catch(() => {});
    const sync = () => setCount(cartCount());
    window.addEventListener("shop-cart-changed", sync);
    return () => window.removeEventListener("shop-cart-changed", sync);
  }, []);

  useEffect(() => {
    const q = active ? `?category_id=${active}` : "";
    api.get(`/public/shop/products${q}`).then((r) => setProducts(r.data.products)).catch((e) => toast.error(apiError(e)));
  }, [active]);

  return (
    <div className="min-h-screen bg-[#FAF7FF]" data-testid="public-shop">
      <header className="sticky top-0 z-20 flex items-center justify-between gap-3 border-b border-pink-100 bg-white/90 px-4 py-3 backdrop-blur">
        <Link to="/" data-testid="shop-back-home" className="flex items-center gap-1.5 text-sm font-bold text-[#7C4DE0]"><CaretLeft size={18} weight="bold" /> Beranda</Link>
        <h1 className="flex items-center gap-2 font-heading text-lg font-extrabold text-[#4C1D95]"><Storefront size={22} weight="duotone" className="text-[#DB2777]" /> Belanja</h1>
        <Link to="/belanja/keranjang" data-testid="shop-cart-link" className="relative flex items-center gap-1.5 rounded-full bg-[#7C4DE0] px-3 py-1.5 text-xs font-bold text-white">
          <ShoppingCart size={16} weight="fill" /> Keranjang
          {count > 0 && <span data-testid="shop-cart-badge" className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#DB2777] px-1 text-[10px] font-extrabold text-white">{count}</span>}
        </Link>
      </header>

      <div className="mx-auto max-w-4xl px-4 py-5">
        <div className="mb-4 flex flex-wrap gap-2" data-testid="shop-category-filter">
          <button data-testid="shop-cat-all" onClick={() => setActive("")} className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${active === "" ? "bg-[#7C4DE0] text-white" : "bg-white text-slate-600 border border-pink-100"}`}>Semua</button>
          {cats.map((c) => (
            <button key={c.id} data-testid={`shop-cat-${c.id}`} onClick={() => setActive(c.id)} className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${active === c.id ? "bg-[#7C4DE0] text-white" : "bg-white text-slate-600 border border-pink-100"}`}>{c.name}</button>
          ))}
        </div>

        {products.length === 0 ? (
          <div className="rounded-2xl border border-pink-100 bg-white p-10 text-center text-sm text-slate-400" data-testid="shop-empty">Belum ada produk tersedia.</div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {products.map((p) => (
              <Link key={p.id} to={`/belanja/produk/${p.id}`} data-testid={`shop-product-${p.id}`} className="group flex flex-col overflow-hidden rounded-2xl border border-pink-100 bg-white transition-transform duration-200 hover:-translate-y-0.5 active:scale-[0.99]">
                <div className="aspect-square w-full overflow-hidden bg-[#F3ECFF]">
                  {p.images[0] ? (
                    <img src={`${API}/public/shop/products/${p.id}/image/${p.images[0].img_id}`} alt={p.name} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-[#C9B6F0]"><Storefront size={40} weight="duotone" /></div>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-2.5">
                  <p className="line-clamp-2 text-sm font-bold leading-tight text-slate-800">{p.name}</p>
                  {p.variants.some((v) => v.discount_active) && (
                    <span data-testid={`shop-product-disc-${p.id}`} className="mt-1 inline-block w-fit rounded-full bg-[#FCE7F3] px-1.5 py-0.5 text-[9px] font-extrabold text-[#DB2777]">DISKON</span>
                  )}
                  <p className="mt-1 text-xs font-extrabold text-[#DB2777]">{priceRange(p.variants)}</p>
                  <p className="mt-0.5 text-[10px] text-slate-400">{p.variants.length} varian</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
